
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { format, differenceInHours, parseISO } from 'date-fns';
import { fr, enUS } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { useUserStore } from '@/store/userStore';
import { supabase } from '@/integrations/supabase/client';
import { getUserProfile } from '@/services/edgeFunctionWrappers/profilesManagementWrapper';
import { cancelBookingWithJoker } from '@/services/edgeFunctionWrappers/bookingsRpcWrapper';
import { cancellationToast, bookingErrorToast } from "@/utils/bookingToast";
import { useState } from "react";
import { BookingData } from '@/types/types';
import { AlertTriangle } from "lucide-react";
import { logUserActivity } from "@/utils/logUserActivity";
import { scheduleClassesService } from '@/services/scheduleClassesService';

interface CancelBookingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  booking: BookingData;
  onConfirm: () => Promise<void>;
}

export function CancelBookingDialog({
  open,
  onOpenChange,
  booking,
  onConfirm,
}: CancelBookingDialogProps) {
  const { t, i18n } = useTranslation(["athlete", "common"]);
  const dateLocale = i18n.language === 'fr' ? fr : enUS;
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useUserStore();

  // Vérifier si l'annulation est moins de 24h avant le cours
  const isWithin24Hours = () => {
    try {
      const bookingDate = parseISO(booking.date);
      const [hours, minutes] = booking.time.split(':').map(Number);
      bookingDate.setHours(hours, minutes);
      
      const now = new Date();
      return differenceInHours(bookingDate, now) < 24;
    } catch (error) {
      console.error('Erreur lors du calcul de la différence de temps:', error);
      return false;
    }
  };
  
  const needsJoker = isWithin24Hours();

  const handleConfirmCancellation = async () => {
    setIsLoading(true);
    try {
      if(user === null)
      {
        throw new Error("No user found !");

      }
      // Récupérer le profil utilisateur pour l'email
      const { data: userProfile } = await getUserProfile(user.id);
      
      // Annuler la réservation avec la gestion automatique des jokers
      const gymId = useUserStore.getState().gymId;
      const result = await cancelBookingWithJoker(booking.id, gymId);

      // Le wrapper renvoie { success: false, data: null } sans lever quand la
      // session a expiré : sans ce test on journalisait une annulation qui
      // n'a pas eu lieu, puis on plantait sur result.data.needs_joker.
      if (!result?.success || !result.data) {
        bookingErrorToast(t('booking.cancelError', { ns: 'athlete' }));
        return;
      }

      logUserActivity("BOOKING_CANCELLED", `bookId:${booking.id},user`);
      
      // Envoyer l'email de confirmation
      if (user) {
        try {
          const formattedDate = format(new Date(booking.date), 'dd/MM/yyyy', { locale: dateLocale });
          
          const { data: emailData, error: emailError } = await supabase.functions.invoke('send-class-email', {
            body: {
              type: "cancellation",
              gym_id: gymId,
              user: {
                email: user.email,
                firstName: userProfile?.first_name,
                lastName: userProfile?.last_name
              },
              classInfo: {
                className: booking.className,
                date: formattedDate,
                time: booking.time,
                coach: booking.coach,
                duration: booking.duration
              },
              jokerInfo: {
                needsJoker: result.data.needs_joker,
                jokerUsed: result.data.joker_used,
                sessionUsed: result.data.session_used
              }
            }
          });

          if (emailError) {
            console.error('Error sending email:', emailError);
            // Don't block the process if email fails
          } else {
            console.log('Email confirmation sent successfully');
          }
        } catch (emailErr) {
          console.error('Exception when sending email:', emailErr);
          // Don't block the process if email fails
        }
      }
      
      // Afficher un toast avec des informations sur les jokers si nécessaire
      if (needsJoker) {
        if (result.data.joker_used) {
          cancellationToast(t('booking.jokerUsed', { ns: 'athlete' }), 'info');
        } else if (result.data.session_used) {
          cancellationToast(t('booking.sessionUsed', { ns: 'athlete' }), 'info');
        } else {
          // Ni joker ni séance consommés : sans ce cas, aucun retour n'était
          // affiché et l'utilisateur ignorait si l'annulation avait abouti.
          cancellationToast(t('booking.cancelSuccess', { ns: 'athlete' }), 'success');
        }
      }
      else{
        cancellationToast(t('booking.cancelSuccess', { ns: 'athlete' }), 'success');
      }
      
      // Invalider le cache pour forcer un refresh du planning
      if (user.id && gymId) {
        scheduleClassesService.invalidateCache(user.id, gymId);
        console.log('[CancelBookingDialog] Cache invalidated after cancellation');
      }
      
      await onConfirm();
    } catch (error) {
      console.error('Erreur lors de l\'annulation:', error);
      bookingErrorToast(t('errors.cancelFailed', { ns: 'athlete' }));
    } finally {
      setIsLoading(false);
      onOpenChange(false);
    }
  };
  
  const formattedDate = format(new Date(booking.date), 'EEEE d MMMM yyyy', { locale: dateLocale });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('booking.cancelTitle', { ns: 'athlete' })}</DialogTitle>
          <DialogDescription>
            {t('booking.cancelDescription', { ns: 'athlete' })}
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div className="space-y-2">
            <h3 className="font-medium">{booking.className}</h3>
            <div className="text-sm text-muted-foreground space-y-1">
              <div>{formattedDate}</div>
              <div>{booking.time} {booking.duration && `(${booking.duration})`}</div>
              <div>{t('booking.coach', { ns: 'athlete' })}: {booking.coach}</div>
            </div>
            
            {needsJoker && (
              <div className="mt-4 flex gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
                <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium">{t('booking.warningJoker', { ns: 'athlete' })}</p>
                  <p className="mt-1">{t('booking.jokerExplanation', { ns: 'athlete' })}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <DialogClose asChild>
            <Button
              variant="outline"
              disabled={isLoading}
            >
              {t('cancel', { ns: 'common' })}
            </Button>
          </DialogClose>
          <Button 
            variant="destructive"
            onClick={handleConfirmCancellation}
            disabled={isLoading}
          >
            {isLoading ? t('loading', { ns: 'common' }) : t('booking.confirmCancel', { ns: 'athlete' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
